// Input router: keyboard, Zwift controllers, gamepads, the touch overlay and a paired phone all feed the same game actions.
// Sessions (race, workout, arcade game) listen to a single `action` event and never see where it came from.

export const GAME_ACTIONS = ['steerLeft', 'steerRight', 'fire', 'gearUp', 'gearDown', 'look', 'camera', 'pause'] as const;
export type GameAction = (typeof GAME_ACTIONS)[number];
export type InputSource = 'keyboard' | 'controller' | 'gamepad' | 'touch' | 'remote';
/** Every source, for a release of everything (a session starting or ending). */
export const INPUT_SOURCES: readonly InputSource[] = ['keyboard', 'controller', 'gamepad', 'touch', 'remote'];
export interface ActionDetail { action: GameAction; down: boolean; source: InputSource }

export const isGameAction = (a: unknown): a is GameAction => typeof a === 'string' && (GAME_ACTIONS as readonly string[]).includes(a);

/** Default keyboard bindings. A key triggers one action; an action can have several keys. */
export const DEFAULT_KEYS: Readonly<Record<string, GameAction>> = {
  arrowleft: 'steerLeft', arrowright: 'steerRight', ' ': 'fire', e: 'gearUp', d: 'gearDown', b: 'look', v: 'camera', p: 'pause',
};

/**
 * Keys the sessions keep for themselves (simulated watts, workout intensity, next block, ERG) or that the browser
 * needs: they can never be bound. Escape always pauses, so a player can never lock themselves out of the menu.
 */
export const RESERVED_KEYS: ReadonlySet<string> = new Set(['escape', 'arrowup', 'arrowdown', '+', '=', '-', '_', 'n', 'r', 'tab', 'enter', 'shift', 'control', 'alt', 'meta', 'capslock', 'contextmenu', 'dead', 'unidentified']);

const MAX_BINDINGS = 64;

/** Normalised key name, as stored in bindings: lower case, Space as ' '. */
export function normKey(key: string): string {
  const k = key.toLowerCase();
  return k === 'spacebar' || k === 'space' ? ' ' : k;
}

/** A key can be bound when it is a single printable character or a named key (F1, ArrowLeft, Home…) that is not reserved. */
export function isBindableKey(key: string): boolean {
  const k = normKey(key);
  if (!k || RESERVED_KEYS.has(k) || k.length > 16) return false;
  return [...k].length === 1 || /^[a-z][a-z0-9]+$/.test(k);
}

/** Bindings read from settings: anything invalid (unknown action, reserved key, junk from an old backup) is dropped. */
export function keyBindings(custom: unknown): Record<string, GameAction> {
  if (!custom || typeof custom !== 'object' || Array.isArray(custom)) return { ...DEFAULT_KEYS };
  const out: Record<string, GameAction> = {};
  for (const [k, a] of Object.entries(custom as Record<string, unknown>).slice(0, MAX_BINDINGS)) if (isBindableKey(k) && isGameAction(a)) out[normKey(k)] = a;
  return out;
}

/** Binds `key` to `action`: the key leaves its previous action, the action keeps its other keys. */
export function bindKey(map: Record<string, GameAction>, key: string, action: GameAction): Record<string, GameAction> {
  const k = normKey(key);
  if (!isBindableKey(k)) return map;
  return { ...map, [k]: action };
}

/** Removes every key of an action (it then only answers to the controllers, gamepad and touch). */
export function unbindAction(map: Record<string, GameAction>, action: GameAction): Record<string, GameAction> {
  return Object.fromEntries(Object.entries(map).filter(([, a]) => a !== action));
}

/** Action of a keyboard event, Escape included. */
export function keyAction(key: string, map: Record<string, GameAction>): GameAction | null {
  const k = normKey(key);
  if (k === 'escape') return 'pause';
  return map[k] ?? null;
}

/** Readable key name for the settings and the pause menu. */
export function keyLabel(key: string, names: { space: string }): string {
  const k = normKey(key);
  const arrows: Record<string, string> = { arrowleft: '←', arrowright: '→', arrowup: '↑', arrowdown: '↓' };
  if (k === ' ') return names.space;
  if (arrows[k]) return arrows[k];
  return k.length === 1 ? k.toUpperCase() : k.charAt(0).toUpperCase() + k.slice(1);
}

/**
 * Merges the physical controls of every source. An action is held as long as one control holds it, so a finger
 * still on the screen is not released by a key going up elsewhere. The `action` event fires on transitions only.
 */
export class InputRouter extends EventTarget {
  private holders = new Map<GameAction, Set<string>>();

  /** `control` names the physical control within its source (a key, a button, a pointer). Returns true on a transition. */
  press(source: InputSource, control: string, action: GameAction, down: boolean): boolean {
    const id = `${source}:${control}`;
    let set = this.holders.get(action);
    if (!set) this.holders.set(action, (set = new Set()));
    const was = set.size > 0;
    if (down) set.add(id); else set.delete(id);
    const now = set.size > 0;
    if (was === now) return false;
    this.dispatchEvent(new CustomEvent<ActionDetail>('action', { detail: { action, down: now, source } }));
    return true;
  }

  isHeld(action: GameAction): boolean { return (this.holders.get(action)?.size ?? 0) > 0; }

  /** Releases what a source holds: window losing focus, gamepad unplugged, overlay removed. */
  releaseSource(source: InputSource) {
    for (const [action, set] of this.holders) {
      const mine = [...set].filter(id => id.startsWith(source + ':'));
      for (const id of mine) this.press(source, id.slice(source.length + 1), action, false);
    }
  }

  releaseAll() { for (const s of INPUT_SOURCES) this.releaseSource(s); }
}

/** Typing in a form field never drives the game. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as { closest?: (s: string) => unknown; isContentEditable?: boolean } | null;
  return !!el && (!!el.isContentEditable || !!el.closest?.('input, select, textarea, [contenteditable]'));
}

export interface KeyLike { key: string; repeat: boolean; ctrlKey: boolean; metaKey: boolean; altKey: boolean; target: EventTarget | null; preventDefault(): void }

/**
 * Feeds the router from keyboard events. Returns true when the key was a game action (the caller then ignores it).
 * Repeats are swallowed: holding a key is one press, as on every other source.
 */
export function routeKey(router: InputRouter, e: KeyLike, down: boolean, map: Record<string, GameAction>): boolean {
  const action = keyAction(e.key, map);
  if (!action) return false;
  const control = normKey(e.key);
  // a release always goes through, whatever the focus or modifiers: otherwise the action would stay stuck
  if (!down) { router.press('keyboard', control, action, false); return true; }
  if (e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return false;
  e.preventDefault();
  if (!e.repeat) router.press('keyboard', control, action, true);
  return true;
}
